// The clock over D1, through the same two-method runner the identity and
// league stores use.
//
// VALIDATE AT THE BOUNDARY. `league_settings_versions.settings_json` and
// `league_periods.windows_json` are both text columns holding shapes this
// package then reasons about, so both are parsed with Zod on the way out. A
// stored settings blob that no longer parses is a refusal with a code, not a
// clock quietly computing a lock from `undefined`.
//
// THE CLAIM IS A CONDITIONAL INSERT and it is the most interesting statement
// in the file. There is no reading between statements inside a batch, so "has
// somebody else got this job" cannot be a SELECT followed by an INSERT — it is
// one INSERT whose WHERE NOT EXISTS does the checking, and `changes` says who
// won. That is the same shape `password_resets.used_at`, `sessions.renewed_at`
// and the seat claim all use, for the same reason. The due rows are the same
// shape again: ON CONFLICT DO NOTHING on a derived key, so planning an instant
// twice leaves one row.

import {
  StoredLeagueSettings,
  type JobOutcomeShape,
  type ScheduledJobShape,
  type ScheduleDocument,
} from '@fsp/domain';
import type { Game } from '@fsp/sports-core';
import { z } from 'zod';
import type { JobBus } from './bus';
import type { StoredPeriod } from './calendar';
import type { DueRow } from './due';
import type { ClockEffects } from './handlers';
import { nextMessageSeq } from './message-seq';
import { createClockService, type ClockPort, type ClockServiceOptions } from './service';
import type { ClockLeague, ClockService, ClockSport, JobHandler, SqlRunner } from './types';
import type { JobKindId } from '@fsp/domain';

/** What `league_periods.windows_json` holds. `firstKickoff` is additive — see calendar.ts. */
const StoredWindowRow = z.object({
  id: z.string().min(1),
  start: z.string().min(1),
  end: z.string().min(1),
  firstKickoff: z.string().nullable().default(null),
});

const PeriodFlags = z.object({
  playoffEligible: z.boolean(),
  proBye: z.array(z.string()),
});

export interface SqlClockOptions {
  sql: SqlRunner;
  /** Sport id to definition. The package never imports a sport. */
  definitions: Record<string, ClockSport>;
  /**
   * The live kinds, or a factory over the effects that produces them.
   *
   * A FACTORY, because the effects are built in here and a caller adding a kind
   * almost always needs them: E3's scoring handlers merge with
   * `standardHandlers(effects)` rather than replacing it, and a caller that
   * replaced it would silently turn the lineup lock off. Passing a bare map
   * still works and still means "exactly these".
   */
  handlers?: Map<JobKindId, JobHandler> | ((effects: ClockEffects) => Map<JobKindId, JobHandler>);
  /** A fresh id for a system message row. Injected so a test can make it stable. */
  newMessageId?: (key: string) => string;
  /** Where a sweep sends its work. Absent, the sweep runs it itself. */
  bus?: JobBus;
  /** What each job's handler runs inside (`ClockServiceOptions.around`): the worker's meter. */
  around?: ClockServiceOptions['around'];
  /**
   * The season's games from its schedule artifact (ICD-S10), for a league in
   * the real world — `ArtifactReader` is one. Absent, or no document to stand
   * on, the table: the suites, a seeded league, a draft's calendar.
   */
  schedules?: {
    schedule(sport: string, season: number): Promise<ScheduleDocument | null>;
    used(doc: ScheduleDocument): void;
  } | null;
}

interface LeagueRow {
  id: string;
  sport: string;
  season: number;
  status: string;
  seeded: string | null;
  frozen_at: string | null;
  settings_json: string;
  bots: number;
}

interface PeriodRow {
  period: number;
  label: string;
  start_at: string;
  end_at: string;
  windows_json: string;
  flags_json: string;
  kind: string;
  season: number;
}

interface GameRow {
  id: string;
  sport: string;
  season: number;
  start_at: string;
  home_team: string;
  away_team: string;
  status: string;
  period_hint: number | null;
}

interface DueRowRow {
  job_id: string;
  league_id: string;
  kind: string;
  due_at: string;
  period: number | null;
  window_id: string | null;
  attempt: number;
  not_before: string;
  enqueued_at: string | null;
  planned_at: string;
}

const LEAGUE_COLUMNS = `
  SELECT l.id, l.sport, l.season, l.status, l.seeded, l.frozen_at, v.settings_json,
         EXISTS (SELECT 1 FROM team_ai_managers a JOIN teams t ON t.id = a.team_id
                  WHERE a.league_id = l.id AND t.owner_user_id IS NULL) AS bots
    FROM leagues l
    JOIN league_settings_versions v
      ON v.league_id = l.id AND v.version = l.settings_version`;

const DUE_COLUMNS = `job_id, league_id, kind, due_at, period, window_id, attempt, not_before, enqueued_at, planned_at`;

function toLeague(row: LeagueRow): ClockLeague | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(row.settings_json);
  } catch {
    return null;
  }
  const settings = StoredLeagueSettings.safeParse(parsed);
  if (!settings.success) return null;
  return {
    id: row.id,
    sport: row.sport,
    season: row.season,
    status: row.status,
    settings: settings.data,
    seeded: row.seeded,
    frozenAt: row.frozen_at,
    bots: row.bots === 1,
  };
}

function toGame(row: GameRow): Game {
  return {
    id: row.id,
    sport: row.sport as Game['sport'],
    season: row.season,
    start: row.start_at,
    homeTeam: row.home_team,
    awayTeam: row.away_team,
    status: row.status as Game['status'],
    ...(row.period_hint === null ? {} : { periodHint: row.period_hint }),
  };
}

function toDue(row: DueRowRow): DueRow {
  return {
    id: row.job_id,
    leagueId: row.league_id,
    kind: row.kind as JobKindId,
    dueAt: row.due_at,
    period: row.period,
    windowId: row.window_id,
    attempt: row.attempt,
    notBefore: row.not_before,
    enqueuedAt: row.enqueued_at,
    plannedAt: row.planned_at,
  };
}

/** `?1, ?2, …` for a list, inside D1's hundred parameters a statement. */
const marks = (n: number): string => Array.from({ length: n }, (_, i) => `?${i + 1}`).join(', ');

/** At most this many ids in one IN list, leaving room for the other parameters. */
const IN_CHUNK = 90;

export function createSqlClock(options: SqlClockOptions): ClockService {
  const { sql } = options;
  const messageId = options.newMessageId ?? ((key: string) => `msg-${key}`);

  const effects: ClockEffects = {
    async lockLineups(leagueId, windowId, at, clubs) {
      // AN EMPTY CLUB LIST LOCKS NOTHING, and never reaches SQL: `IN ()` is a
      // syntax error, and "no games have started" is a real answer rather than
      // an edge case.
      if (clubs !== null && clubs.length === 0) return 0;
      // AT MOST THIRTY-TWO CLUBS, so the parameter count stays well inside
      // D1's hundred per statement even with the four fixed parameters.
      //
      // FROM THE LEAGUE'S TEAMS, each team's slots by its key, and each
      // slot's player by his (PLAN-2026-10-10 chunk 0). The clubs were found
      // through `SELECT id FROM players WHERE pro_team IN (…)`, which reads
      // the whole directory — `players` has no index on the club — and the
      // planner walked the week's slots of every league to meet it: 6,055
      // rows for one league's lock in production, growing with the leagues.
      // `CROSS JOIN` fixes the order, as the week's sides read it.
      const narrow =
        clubs === null
          ? ''
          : ` AND (SELECT p.pro_team FROM players p WHERE p.id = ls.player_id) IN (${clubs
              .map(() => '?')
              .join(', ')})`;
      // THE DESIGNATION AT LOCK goes down with the lock (`0034`): an achievement
      // for a starter's bad day counts only a fair start (ACHIEVEMENTS.md §8),
      // and `players.injury_status` keeps only today's.
      const { changes } = await sql.run(
        `UPDATE lineup_slots
            SET locked_at = ?, updated_at = ?,
                locked_designation = (SELECT injury_status FROM players WHERE id = lineup_slots.player_id)
          WHERE rowid IN (
                  SELECT ls.rowid
                    FROM teams t CROSS JOIN lineup_slots ls
                      ON ls.team_id = t.id AND ls.window_id = ?
                   WHERE t.league_id = ? AND ls.locked_at IS NULL${narrow})`,
        [at.toISOString(), at.toISOString(), windowId, leagueId, ...(clubs ?? [])],
      );
      return changes;
    },
    async setLeagueStatus(leagueId, status, at) {
      const { changes } = await sql.run(
        `UPDATE leagues SET status = ?, updated_at = ? WHERE id = ? AND status <> ?`,
        [status, at.toISOString(), leagueId, status],
      );
      return changes > 0;
    },
    async carryLineups(leagueId, toWindowId, from, at) {
      if (from.length === 0) return 0;
      // Which earlier windows each team has a player in; the order is
      // `from`'s. A window of empty cells is not a lineup to carry.
      const held = await sql.all<{ team_id: string; window_id: string }>(
        `SELECT DISTINCT ls.team_id, ls.window_id
           FROM lineup_slots ls
           JOIN teams t ON t.id = ls.team_id
          WHERE t.league_id = ? AND ls.window_id <> ? AND ls.player_id IS NOT NULL`,
        [leagueId, toWindowId],
      );
      const windowsOf = new Map<string, Set<string>>();
      for (const r of held) {
        const set = windowsOf.get(r.team_id) ?? new Set<string>();
        set.add(r.window_id);
        windowsOf.set(r.team_id, set);
      }
      let carried = 0;
      for (const [teamId, windows] of windowsOf) {
        const source = from.find((w) => windows.has(w));
        if (source === undefined) continue;
        // ONE STATEMENT PER TEAM, and every rule of the carry is in it: still
        // on the roster, a cell nobody has filled, and a player the new window
        // has not already placed somewhere else. AN EMPTY, UNLOCKED CELL IS
        // NOBODY'S CHOICE — the local seeder writes a week's empty cells as
        // rows, and the API's save deletes a cell rather than emptying it — so
        // it is filled, by the same upsert the draft's first lineup uses
        // (`draft.ts`).
        const { changes } = await sql.run(
          `INSERT INTO lineup_slots
             (team_id, window_id, slot, slot_index, player_id, locked_at, set_by, updated_at)
           SELECT s.team_id, ?, s.slot, s.slot_index, s.player_id, NULL, 'carried', ?
             FROM lineup_slots s
            WHERE s.team_id = ? AND s.window_id = ? AND s.player_id IS NOT NULL
              AND EXISTS (SELECT 1 FROM roster_players r
                           WHERE r.team_id = s.team_id AND r.player_id = s.player_id)
              AND NOT EXISTS (SELECT 1 FROM lineup_slots n
                               WHERE n.team_id = s.team_id AND n.window_id = ?
                                 AND n.slot = s.slot AND n.slot_index = s.slot_index
                                 AND (n.player_id IS NOT NULL OR n.locked_at IS NOT NULL))
              AND NOT EXISTS (SELECT 1 FROM lineup_slots n
                               WHERE n.team_id = s.team_id AND n.window_id = ?
                                 AND n.player_id = s.player_id)
           ON CONFLICT (team_id, window_id, slot, slot_index) DO UPDATE
              SET player_id = excluded.player_id, set_by = 'carried',
                  updated_at = excluded.updated_at
            WHERE lineup_slots.player_id IS NULL AND lineup_slots.locked_at IS NULL`,
          [toWindowId, at.toISOString(), teamId, source, toWindowId, toWindowId],
        );
        if (changes > 0) carried += 1;
      }
      return carried;
    },
    async postSystemMessage(leagueId, key, body, at) {
      // OR IGNORE on a derived primary key: posting the same fact twice leaves
      // one message rather than two identical sentences in the league chat.
      await sql.run(
        `INSERT OR IGNORE INTO messages
           (id, league_id, channel, author_id, kind, body_kind, body,
            refs_json, reactions_json, pinned, created_at, seq)
         VALUES (?1, ?2, 'league', NULL, 'system', 'text', ?3, '[]', '{}', 0, ?4,
                 ${nextMessageSeq('?2')})`,
        [messageId(key), leagueId, body, at.toISOString()],
      );
    },
  };

  const handlers =
    typeof options.handlers === 'function' ? options.handlers(effects) : options.handlers;

  const port: ClockPort = {
    definitionFor: (sport) => options.definitions[sport] ?? null,

    league: async (leagueId) => {
      const rows = await sql.all<LeagueRow>(`${LEAGUE_COLUMNS} WHERE l.id = ?`, [leagueId]);
      return rows.length > 0 ? toLeague(rows[0]!) : null;
    },

    gamesFor: async (league) => {
      // FROM THE SEASON'S SCHEDULE when it stands, for a league in the real
      // world: the week strip and every planning job read the season's games,
      // 286 rows of the table on the dev database (PLAN-2026-10-10).
      if (league.seeded === null && options.schedules) {
        const doc = await options.schedules.schedule(league.sport, league.season);
        if (doc !== null && doc.season === league.season) {
          options.schedules.used(doc);
          return doc.games.map((g) =>
            toGame({
              id: g.id,
              sport: league.sport,
              season: league.season,
              start_at: g.startAt,
              home_team: g.home,
              away_team: g.away,
              status: g.status,
              period_hint: g.periodHint,
            }),
          );
        }
      }
      // `IS NOT 'sim'`, so a row written before `games.source` existed (NULL)
      // reads as the real schedule it is. Only the local seeder writes 'sim'.
      const rows = await sql.all<GameRow>(
        `SELECT id, sport, season, start_at, home_team, away_team, status, period_hint
           FROM games
          WHERE sport = ? AND season = ? AND (? = 1 OR source IS NOT 'sim')
          ORDER BY start_at`,
        [league.sport, league.season, league.seeded === null ? 0 : 1],
      );
      // Two games at one kickoff by id, as the document holds them, sorted
      // here so the statement keeps the index's order and needs no sort.
      return rows
        .sort((a, b) =>
          a.start_at === b.start_at ? (a.id < b.id ? -1 : 1) : a.start_at < b.start_at ? -1 : 1,
        )
        .map(toGame);
    },

    linesLanded: async (sport, since, now) => {
      // `landings`, written by the stats run beside its ledger row
      // (`landingStatements`): the app's own copy of the one fact it needs
      // from the ingest ledger, which leaves with the fact store (§4.10).
      const rows = await sql.all<{ finished_at: string }>(
        `SELECT finished_at FROM landings
          WHERE sport = ? AND rows_written > 0 AND finished_at > ? AND finished_at <= ?
          ORDER BY finished_at`,
        [sport, since.toISOString(), now.toISOString()],
      );
      return rows.map((r) => r.finished_at);
    },

    storedPeriods: async (leagueId) => {
      // The season comes from the league rather than from the row: a period
      // belongs to exactly one league's season and storing it twice is two
      // places to disagree.
      const rows = await sql.all<PeriodRow>(
        `SELECT p.period, p.label, p.start_at, p.end_at, p.windows_json, p.flags_json,
                p.kind, l.season
           FROM league_periods p
           JOIN leagues l ON l.id = p.league_id
          WHERE p.league_id = ? ORDER BY p.period`,
        [leagueId],
      );
      const out: StoredPeriod[] = [];
      for (const row of rows) {
        const windows = z.array(StoredWindowRow).safeParse(JSON.parse(row.windows_json));
        const flags = PeriodFlags.safeParse(JSON.parse(row.flags_json));
        // A row we cannot read is a row we do not pretend to have read. It is
        // skipped and the calendar is rebuilt over it on the next materialize,
        // which is the recoverable answer — throwing here would take a whole
        // league's clock down for one bad blob.
        if (!windows.success || !flags.success) continue;
        out.push({
          season: row.season,
          index: row.period,
          label: row.label,
          start: row.start_at,
          end: row.end_at,
          kind: row.kind as StoredPeriod['kind'],
          windows: windows.data,
          flags: { playoffEligible: flags.data.playoffEligible, proBye: flags.data.proBye },
        });
      }
      return out;
    },

    writePeriods: async (leagueId, periods) => {
      // One transaction: a calendar half-replaced is a season with a hole in
      // it, and the window between the delete and the insert is exactly when a
      // reader would see it.
      await sql.batch([
        { sql: `DELETE FROM league_periods WHERE league_id = ?`, params: [leagueId] },
        ...periods.map((p) => ({
          sql: `INSERT INTO league_periods
                  (league_id, period, label, start_at, end_at, windows_json, flags_json, kind)
                VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          params: [
            leagueId,
            p.index,
            p.label,
            p.start,
            p.end,
            JSON.stringify(p.windows),
            JSON.stringify(p.flags),
            p.kind,
          ],
        })),
      ]);
    },

    lastSweepAt: async () => {
      const rows = await sql.all<{ swept_at: string }>(
        `SELECT swept_at FROM clock_sweeps WHERE scope = 'global'`,
      );
      return rows.length > 0 ? new Date(rows[0]!.swept_at) : null;
    },

    recordSweep: async (sweptAt, enqueued) => {
      // `cursor` (0038) stays NULL: the pass it marked is gone with Phase 1.
      await sql.run(
        `INSERT INTO clock_sweeps (scope, swept_at, jobs_run, cursor) VALUES ('global', ?, ?, NULL)
           ON CONFLICT(scope) DO UPDATE SET swept_at = excluded.swept_at,
                                            jobs_run = excluded.jobs_run,
                                            cursor = NULL`,
        [sweptAt.toISOString(), enqueued],
      );
    },

    claimMinute: async (now) => {
      // THE OVERLAP GUARD, ON ONE ROW. Won only by an instant later than the
      // latest sweep's: a replayed scheduled event, or one older than a sweep
      // already started, changes nothing and learns so from `changes`.
      const { changes } = await sql.run(
        `INSERT INTO clock_sweeps (scope, swept_at, jobs_run) VALUES ('heartbeat', ?, 0)
           ON CONFLICT(scope) DO UPDATE SET swept_at = excluded.swept_at
            WHERE clock_sweeps.swept_at < excluded.swept_at`,
        [now.toISOString()],
      );
      return changes > 0;
    },

    settled: async (leagueId, since, now) => {
      const rows = await sql.all<{ id: string }>(
        `SELECT DISTINCT id FROM clock_jobs
          WHERE league_id = ? AND due_at > ? AND due_at <= ?
            AND status IN ('ok', 'idle', 'partial', 'refused')`,
        [leagueId, since.toISOString(), now.toISOString()],
      );
      return new Set(rows.map((r) => r.id));
    },

    failed: async (leagueId, since, now) => {
      // THE LATEST ATTEMPT ONLY. A job that failed once and then went `ok` is
      // done, and one still `running` under a live lease is somebody's.
      const rows = await sql.all<{
        id: string;
        league_id: string;
        kind: string;
        due_at: string;
        period: number | null;
        window_id: string | null;
      }>(
        `SELECT j.id, j.league_id, j.kind, j.due_at, j.period, j.window_id
           FROM clock_jobs j
          WHERE j.league_id = ? AND j.due_at > ? AND j.due_at <= ?
            AND j.status = 'failed'
            AND j.attempt = (SELECT MAX(k.attempt) FROM clock_jobs k WHERE k.id = j.id)
          ORDER BY j.due_at`,
        [leagueId, since.toISOString(), now.toISOString()],
      );
      return rows.map((r) => ({
        id: r.id,
        leagueId: r.league_id,
        kind: r.kind as JobKindId,
        dueAt: r.due_at,
        period: r.period,
        windowId: r.window_id,
      }));
    },

    claim: async (job: ScheduledJobShape, now, leaseUntil) => {
      const { changes } = await sql.run(
        `INSERT INTO clock_jobs
           (id, league_id, kind, due_at, period, window_id, attempt,
            status, ran_at, late_by, lease_until, note)
         SELECT ?, ?, ?, ?, ?, ?,
                COALESCE((SELECT MAX(attempt) FROM clock_jobs WHERE id = ?), 0) + 1,
                'running', ?, 0, ?, NULL
          WHERE NOT EXISTS (
                SELECT 1 FROM clock_jobs
                 WHERE id = ? AND status = 'running' AND lease_until > ?)`,
        [
          job.id,
          job.leagueId,
          job.kind,
          job.dueAt,
          job.period,
          job.windowId,
          job.id,
          now.toISOString(),
          leaseUntil.toISOString(),
          job.id,
          now.toISOString(),
        ],
      );
      if (changes === 0) return { won: false, attempt: 0 };
      const rows = await sql.all<{ attempt: number }>(
        `SELECT MAX(attempt) AS attempt FROM clock_jobs WHERE id = ?`,
        [job.id],
      );
      return { won: true, attempt: rows[0]?.attempt ?? 1 };
    },

    settle: async (outcome: JobOutcomeShape) => {
      await sql.run(
        `UPDATE clock_jobs
            SET status = ?, ran_at = ?, late_by = ?, note = ?, lease_until = NULL
          WHERE id = ? AND attempt = ?`,
        [
          outcome.status,
          outcome.ranAt,
          outcome.lateBy,
          outcome.note,
          outcome.jobId,
          outcome.attempt,
        ],
      );
    },

    reclaimExpired: async (now) => {
      const { changes } = await sql.run(
        `UPDATE clock_jobs
            SET status = 'failed', lease_until = NULL,
                note = 'The lease expired — a sweep stopped without finishing this.'
          WHERE status = 'running' AND (lease_until IS NULL OR lease_until <= ?)`,
        [now.toISOString()],
      );
      return changes;
    },

    writeDue: async (rows, plannedTo) => {
      // ONE BATCH: the rows and the mark that they are planned to `to`. A row
      // already there stays as it is — the ledger's key, so a job planned
      // twice is one row, and a failed one keeps its backoff.
      const statements = rows.map((r) => ({
        sql: `INSERT INTO clock_due (${DUE_COLUMNS})
              VALUES (?1, ?2, ?3, ?4, ?5, ?6, ?7, ?8, NULL, ?9)
              ON CONFLICT (job_id) DO NOTHING`,
        params: [
          r.id,
          r.leagueId,
          r.kind,
          r.dueAt,
          r.period,
          r.windowId,
          r.attempt,
          r.notBefore,
          r.plannedAt,
        ],
      }));
      if (plannedTo !== null) {
        statements.push({
          sql: `UPDATE leagues SET clock_planned_to = ?2 WHERE id = ?1`,
          params: [plannedTo.leagueId, plannedTo.to.toISOString()],
        });
      }
      if (statements.length === 0) return 0;
      const results = await sql.batch(statements);
      return results.slice(0, rows.length).reduce((n, r) => n + (r.changes > 0 ? 1 : 0), 0);
    },

    due: async (now, staleBefore, limit) => {
      // READY, NOT OUT, AND OF A LEAGUE WITH NOTHING OUT: a league whose rows
      // are with a consumer waits for it, so its jobs never run in two
      // invocations at once (§7.6). In league order, then due order, so a
      // league's rows sit together and one message carries them.
      const rows = await sql.all<DueRowRow>(
        `SELECT ${DUE_COLUMNS} FROM clock_due d
          WHERE d.not_before <= ?1
            AND (d.enqueued_at IS NULL OR d.enqueued_at <= ?2)
            AND NOT EXISTS (SELECT 1 FROM clock_due o
                             WHERE o.league_id = d.league_id AND o.enqueued_at > ?2)
          ORDER BY d.league_id, d.due_at, d.job_id
          LIMIT ?3`,
        [now.toISOString(), staleBefore.toISOString(), limit],
      );
      return rows.map(toDue);
    },

    markEnqueued: async (ids, at) => {
      for (let i = 0; i < ids.length; i += IN_CHUNK) {
        const chunk = ids.slice(i, i + IN_CHUNK);
        await sql.run(
          `UPDATE clock_due SET enqueued_at = ?${chunk.length + 1} WHERE job_id IN (${marks(chunk.length)})`,
          [...chunk, at.toISOString()],
        );
      }
    },

    dueRows: async (ids) => {
      const out: DueRow[] = [];
      for (let i = 0; i < ids.length; i += IN_CHUNK) {
        const chunk = ids.slice(i, i + IN_CHUNK);
        const rows = await sql.all<DueRowRow>(
          `SELECT ${DUE_COLUMNS} FROM clock_due WHERE job_id IN (${marks(chunk.length)})`,
          [...chunk],
        );
        out.push(...rows.map(toDue));
      }
      return out;
    },

    settleDue: async (id) => {
      await sql.run(`DELETE FROM clock_due WHERE job_id = ?`, [id]);
    },

    deferDue: async (id, attempt, notBefore) => {
      await sql.run(
        `UPDATE clock_due SET attempt = ?2, not_before = ?3, enqueued_at = NULL WHERE job_id = ?1`,
        [id, attempt, notBefore.toISOString()],
      );
    },

    releaseDue: async (ids) => {
      for (let i = 0; i < ids.length; i += IN_CHUNK) {
        const chunk = ids.slice(i, i + IN_CHUNK);
        await sql.run(
          `UPDATE clock_due SET enqueued_at = NULL WHERE job_id IN (${marks(chunk.length)})`,
          [...chunk],
        );
      }
    },

    effects,
  };

  return createClockService({ port, handlers, bus: options.bus, around: options.around });
}
